import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Currency,
  type EconomySummary,
  type EconomySummaryBucket,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { getEconomySummary } from '../../services/economy';
import { ApiError } from '../../services/api';
import { useToast } from '../../stores/toastStore';

const DEFAULT_RANGE_DAYS = 7;

const CURRENCY_OPTIONS: ReadonlyArray<{
  value: 'all' | Currency;
  label: string;
}> = [
  { value: 'all', label: 'All currencies' },
  { value: Currency.Energy, label: 'Energy' },
  { value: Currency.Coins, label: 'Coins' },
  { value: Currency.Gems, label: 'Gems' },
  { value: Currency.RealMoney, label: 'Real money' },
];

// Tailwind slate + semantic credit/debit colors. Kept as literals so recharts
// (an SVG lib that doesn't read Tailwind) renders with the same hues.
const COLOR_CREDIT = '#16a34a'; // emerald-600
const COLOR_DEBIT = '#dc2626';  // red-600

/** Format an ISO / YYYY-MM-DD date into a short locale label for the chart/table. */
function fmtDate(iso: string): string {
  try {
    // Parse the date portion as UTC midnight so locale conversion doesn't shift
    // buckets across day boundaries in eastern timezones.
    const d = new Date(`${iso.slice(0, 10)}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

/** Format a YYYY-MM-DD string. Accepts Date. */
function isoDay(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange(): { from: string; to: string } {
  const now = new Date();
  const todayUtc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  const from = new Date(todayUtc);
  from.setUTCDate(from.getUTCDate() - (DEFAULT_RANGE_DAYS - 1));
  return { from: isoDay(from), to: isoDay(todayUtc) };
}

function parseCurrency(raw: string | null): 'all' | Currency {
  if (raw === Currency.Energy) return Currency.Energy;
  if (raw === Currency.Coins) return Currency.Coins;
  if (raw === Currency.Gems) return Currency.Gems;
  if (raw === Currency.RealMoney) return Currency.RealMoney;
  return 'all';
}

function isValidDay(s: string | null): s is string {
  if (!s) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime());
}

interface ChartPoint {
  day: string;    // YYYY-MM-DD
  dayLabel: string;
  credits: number;
  debits: number;
}

/**
 * Collapse buckets into one point per day for the chart. When currency='all'
 * we aggregate ALL currencies into a single (credits, debits) pair — the
 * resulting totals mix units (gems + coins + energy + real_money) which is
 * acceptable for a dashboard overview but not a ledger.
 */
function toChartPoints(
  buckets: readonly EconomySummaryBucket[],
  currencyFilter: 'all' | Currency,
): ChartPoint[] {
  const byDay = new Map<string, { credits: number; debits: number }>();
  for (const b of buckets) {
    if (currencyFilter !== 'all' && b.currency !== currencyFilter) continue;
    const entry = byDay.get(b.day) ?? { credits: 0, debits: 0 };
    entry.credits += b.credits;
    entry.debits += b.debits;
    byDay.set(b.day, entry);
  }
  return Array.from(byDay.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([day, v]) => ({
      day,
      dayLabel: fmtDate(day),
      credits: v.credits,
      debits: v.debits,
    }));
}

/** Sort buckets for the table: day desc, then currency asc, then type asc. */
function sortedBuckets(
  buckets: readonly EconomySummaryBucket[],
): EconomySummaryBucket[] {
  return [...buckets].sort((a, b) => {
    if (a.day !== b.day) return a.day < b.day ? 1 : -1; // day desc
    if (a.currency !== b.currency)
      return a.currency < b.currency ? -1 : 1; // currency asc
    if (a.type !== b.type) return a.type < b.type ? -1 : 1; // type asc
    return 0;
  });
}

/**
 * Admin economy dashboard. Shows day-by-day currency flow with a
 * credits-vs-debits bar chart and a breakdown table grouped by
 * (day, currency, type).
 *
 * Filter state is held in the URL query string so views are shareable and
 * survive a reload. Refetch keys on `(from, to, currency)`.
 */
export default function EconomySummaryScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const defaults = useMemo(() => defaultRange(), []);

  const fromRaw = searchParams.get('from');
  const toRaw = searchParams.get('to');
  const from = isValidDay(fromRaw) ? fromRaw : defaults.from;
  const to = isValidDay(toRaw) ? toRaw : defaults.to;
  const currency = parseCurrency(searchParams.get('currency'));

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: EconomySummary }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const reqCounterRef = useRef(0);
  const fetchSummary = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await getEconomySummary({ from, to, currency });
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load economy summary';
      setState({ status: 'error', message });
      // Surface VALIDATION_ERROR (e.g. from > to) as a toast so the filter row
      // still renders and the admin can correct the bad range in place.
      if (err instanceof ApiError) {
        showToast(`${err.error}: ${err.message}`, 'error');
      }
    }
  }, [from, to, currency, showToast]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  }

  function handleFromChange(next: string) {
    // Empty value → restore the default (avoid serialising `?from=`).
    patchParams({ from: next || defaults.from });
  }
  function handleToChange(next: string) {
    patchParams({ to: next || defaults.to });
  }
  function handleCurrencyChange(next: 'all' | Currency) {
    patchParams({ currency: next === 'all' ? null : next });
  }

  const buckets = useMemo<readonly EconomySummaryBucket[]>(
    () => (state.status === 'ready' ? state.data.buckets : []),
    [state],
  );
  const chartPoints = useMemo(
    () => toChartPoints(buckets, currency),
    [buckets, currency],
  );
  const tableRows = useMemo(() => sortedBuckets(buckets), [buckets]);

  const columns: DataTableColumn<EconomySummaryBucket>[] = useMemo(
    () => [
      {
        key: 'day',
        header: 'Day',
        render: (b) => (
          <span className="text-slate-700">{fmtDate(b.day)}</span>
        ),
      },
      {
        key: 'currency',
        header: 'Currency',
        render: (b) => (
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wide">
            {b.currency}
          </span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        render: (b) => (
          <span className="font-mono text-xs text-slate-700">{b.type}</span>
        ),
      },
      {
        key: 'credits',
        header: 'Credits',
        render: (b) => (
          <span className="font-mono text-emerald-700">
            {b.credits.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'debits',
        header: 'Debits',
        render: (b) => (
          <span className="font-mono text-red-700">
            {b.debits.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'count',
        header: 'Count',
        render: (b) => (
          <span className="font-mono text-slate-700">
            {b.count.toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  const rowKey = (b: EconomySummaryBucket) => `${b.day}|${b.currency}|${b.type}`;

  return (
    <div className="space-y-4" data-testid="economy-summary-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Economy</h1>
      </div>

      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          From
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => handleFromChange(e.target.value)}
            data-testid="economy-filter-from"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>
        <label className="flex flex-col text-xs text-slate-600">
          To
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => handleToChange(e.target.value)}
            data-testid="economy-filter-to"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>
        <label className="flex flex-col text-xs text-slate-600">
          Currency
          <select
            value={currency}
            onChange={(e) =>
              handleCurrencyChange(e.target.value as 'all' | Currency)
            }
            data-testid="economy-filter-currency"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="economy-summary-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <section
        className="bg-white rounded border border-slate-200 p-3"
        data-testid="economy-chart-container"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-900">
            Daily flow
          </h2>
          <div className="text-xs text-slate-500">
            {currency === 'all'
              ? 'Aggregated across all currencies'
              : `Filtered to ${currency}`}
          </div>
        </div>
        {state.status === 'loading' ? (
          <div
            className="h-[320px] flex items-center justify-center text-sm text-slate-500"
            data-testid="economy-chart-loading"
          >
            Loading…
          </div>
        ) : chartPoints.length === 0 ? (
          <div
            className="h-[320px] flex items-center justify-center text-sm text-slate-500"
            data-testid="economy-chart-empty"
          >
            No activity in this range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={chartPoints}
              margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e2e8f0" /* slate-200 */
              />
              <XAxis dataKey="dayLabel" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="credits" fill={COLOR_CREDIT} name="Credits" />
              <Bar dataKey="debits" fill={COLOR_DEBIT} name="Debits" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

      <section data-testid="economy-buckets-section">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">
          Breakdown by day, currency, and type
        </h2>
        <DataTable<EconomySummaryBucket>
          columns={columns}
          rows={tableRows}
          rowKey={rowKey}
          loading={state.status === 'loading'}
          empty="No activity in this range."
          testId="economy-buckets-table"
        />
      </section>
    </div>
  );
}
